import React, { useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'umi';
import { Justify } from 'tea-component';
import { scrollable } from 'tea-component/lib/table/addons';

import RenderOperations from '@sg/components/Chart/Operations';
import Chart from '@sg/components/Chart';
import TableContent from '@sg/components/TableContent';
import Loading from '@intelligence/components/Loading';

import { pickOperations } from '@sg/commonData/pickOperations';
import { rendCardTitle } from '../../utils/rendCardTitle';
import { mathFormat } from '@sg/utils/common';
import { xAxisHighLight, applyValueAxisBoundaryGap } from '@sg/utils/chatUtils';
import { appendStackedTotalLabelSeries } from '@/pages/strategic/utils/stackedTotalLabel';

import type { TInDevChartViewKey } from '../../../../hooks/useInDevChartEyesState';
import {
  getFinancialsColumns,
  getInDevCardChartHeight,
  getInDevCardTableBodyMaxHeight,
  showFinancialsList,
} from '../../../../pageConfig';
import { useSpendTrendData } from './hooks/useSpendTrendData';

interface ISpendTrendCardProps {
  projectId: string;
  activeDateTypeSegment: string;
  selectedMonth?: string | null;
  smallChartViewState: Record<TInDevChartViewKey, boolean>;
  updateInDevEyesViewState: (viewKey: TInDevChartViewKey, enabled: boolean) => void;
  projectInfo: string;
  onPhaseScrollableChange?: (scrollable: boolean) => void;
}

const SpendTrendCard: React.FC<ISpendTrendCardProps> = ({
  projectId,
  activeDateTypeSegment,
  selectedMonth,
  smallChartViewState,
  updateInDevEyesViewState,
  projectInfo,
  onPhaseScrollableChange,
}) => {
  const { formatMessage } = useIntl();

  const {
    tableData,
    chartData,
    updateTime,
    loading,
    showChatsType,
    handleChatsTypeChange,
    isPhaseScrollable,
  } = useSpendTrendData({
    projectId,
    selectedMonth,
    activeDateTypeSegment,
    projectInfo,
    viewEnabled: !!smallChartViewState.titleSpend,
    updateInDevEyesViewState,
  });

  const columns = getFinancialsColumns(activeDateTypeSegment, formatMessage);

  const chartHeight = useMemo(() => getInDevCardChartHeight(), []);
  const tableBodyMaxHeight = useMemo(
    () => getInDevCardTableBodyMaxHeight({ tableHeaderRows: 1 }),
    [],
  );
  const tableAddons = useMemo(
    () => [scrollable({ maxHeight: tableBodyMaxHeight })],
    [tableBodyMaxHeight],
  );

  const spendTrendPhaseZoomWindow = useMemo(() => {
    const visibleCount = 5;
    const totalCount = tableData.length;
    if (!totalCount) {
      return {
        startValue: 0,
        endValue: visibleCount - 1,
      };
    }

    let currentIndex = -1;
    // Find the current point from backend for initial viewport anchoring.
    for (let i = 0; i < totalCount; i += 1) {
      if (tableData?.[i]?.isCurrent === 1) {
        currentIndex = i;
        break;
      }
    }

    const maxStart = Math.max(totalCount - visibleCount, 0);
    // Product rule: start 2 bars before the current item.
    const preferredStart = currentIndex >= 0 ? currentIndex - 2 : 0;
    // Clamp to valid range to avoid out-of-bounds start/end indexes.
    const startValue = Math.min(Math.max(preferredStart, 0), maxStart);
    const endValue = Math.min(startValue + visibleCount - 1, totalCount - 1);

    return {
      startValue,
      endValue,
    };
  }, [tableData]);

  // Persist current spend-trend zoom window so toggling view options
  // (e.g. eye icon) does not jump back to default start/end position.
  const spendTrendZoomWindowRef = useRef<{
    startValue: number;
    endValue: number;
  } | null>(null);

  // Reset persisted zoom window when data scope changes.
  useEffect(() => {
    spendTrendZoomWindowRef.current = null;
  }, [projectId, selectedMonth, activeDateTypeSegment]);

  useEffect(() => {
    onPhaseScrollableChange?.(isPhaseScrollable);
  }, [isPhaseScrollable, onPhaseScrollableChange]);

  const bindSpendTrendZoomSync = (chartInstance: any): void => {
    if (activeDateTypeSegment !== 'phase' || !chartInstance?.on) {
      return;
    }

    if ((chartInstance as any).__spendTrendZoomBound) {
      return;
    }

    const updateZoomWindow = () => {
      const model = chartInstance?.getModel?.();
      const sliderDataZoom = model?.getComponent?.('dataZoom', 1)
        || model?.getComponent?.('dataZoom', 0);
      const startValue = Number(sliderDataZoom?.option?.startValue);
      const endValue = Number(sliderDataZoom?.option?.endValue);
      if (Number.isFinite(startValue) && Number.isFinite(endValue)) {
        spendTrendZoomWindowRef.current = {
          startValue,
          endValue,
        };
      }
    };

    chartInstance.on('datazoom', updateZoomWindow);
    updateZoomWindow();
    (chartInstance as any).__spendTrendZoomBound = true;
  };

  return (
    <div className="dbs-by-date-card">
      <Justify
        bottom
        left={rendCardTitle({
          title: `In Dev Title Spend Trend ($)`,
          tips: updateTime ? `${formatMessage({ id: 'dbs_update_on' })} ${updateTime}` : ''
        })}
        right={<RenderOperations
          key={`spend-ops-${showChatsType.value}-${showChatsType.format}`}
          defaultValue={showChatsType.value}
          defaultValueShow={showChatsType.format}
          change={handleChatsTypeChange}
          options={pickOperations(['view', ...showFinancialsList, 'download'], formatMessage)}
          downloadParams={{
            data: tableData,
            columns,
            name: `In Dev Title Spend Trend`,
          }}>
        </RenderOperations>}
      />

      {loading && (
        <Loading style={{ height: chartHeight }} />
      )}
      {!loading && showChatsType?.value !== 'table' && chartData.seriesData &&
        <Chart
          data={chartData}
          key={`Financials${showChatsType?.value}`}
          type={showChatsType?.value}
          height={chartHeight}
          onChartReady={bindSpendTrendZoomSync}
          setOtherConfigFunc={(opt: any) => {
            opt.grid.containLabel = true;
            opt.legend.show = true;
            if (activeDateTypeSegment === 'phase') {
              // Keep long phase names from consuming too much vertical space.
              const primaryXAxis = Array.isArray(opt?.xAxis)
                ? opt.xAxis?.[0]
                : opt?.xAxis;
              if (primaryXAxis) {
                primaryXAxis.axisLabel = {
                  ...(primaryXAxis.axisLabel || {}),
                  margin: 8,
                  formatter: (label: string) => {
                    if (!label) {
                      return '';
                    }
                    return label.length > 30
                      ? `${label.slice(0, 30)}...`
                      : label;
                  },
                };
              }

              const xAxisData = Array.isArray(opt?.xAxis)
                ? opt?.xAxis?.[0]?.data || []
                : opt?.xAxis?.data || [];
              // Show 5 phase bars by default; keep the rest scrollable.
              if (isPhaseScrollable) {
                // Prefer user's latest scrolled window; fall back to default.
                const visibleCount = 5;
                const totalCount = xAxisData.length;
                const maxStart = Math.max(totalCount - visibleCount, 0);
                const persistedStartValue =
                  spendTrendZoomWindowRef.current?.startValue;
                const rawStartValue = Number.isFinite(persistedStartValue as number)
                  ? Number(persistedStartValue)
                  : spendTrendPhaseZoomWindow.startValue;
                const resolvedStartValue = Math.min(
                  Math.max(rawStartValue, 0),
                  maxStart,
                );
                const resolvedEndValue = Math.min(
                  resolvedStartValue + visibleCount - 1,
                  totalCount - 1,
                );

                // Reserve independent lanes for labels, legend and scrollbar.
                opt.grid.bottom = 60;
                opt.legend.bottom = 20;
                opt.dataZoom = [
                  {
                    type: 'inside',
                    xAxisIndex: 0,
                    startValue: resolvedStartValue,
                    endValue: resolvedEndValue,
                    // Lock to fixed 5 bars: allow pan only, disable zoom resize.
                    zoomLock: true,
                    zoomOnMouseWheel: false,
                    moveOnMouseMove: true,
                    moveOnMouseWheel: true,
                  },
                  {
                    type: 'slider',
                    xAxisIndex: 0,
                    startValue: resolvedStartValue,
                    endValue: resolvedEndValue,
                    zoomLock: true,
                    showDetail: false,
                    showDataShadow: false,
                    height: 8,
                    bottom: 0,
                    borderColor: 'transparent',
                    backgroundColor: '#fff',
                    fillerColor: '#D8DAE4',
                    moveHandleSize: 0,
                    handleSize: 6,
                    handleIcon:
                      'path://M50,0A50,50,0,1,1,50,100A50,50,0,1,1,50,0Z',
                    handleStyle: {
                      color: '#D8DAE4',
                      borderColor: '#D8DAE4',
                    },
                    brushSelect: false,
                  },
                ];
              } else {
                opt.legend.bottom = 0;
                delete opt.dataZoom;
              }
            } else {
              opt.legend.bottom = 0;
              delete opt.dataZoom;
            }
            opt = xAxisHighLight(opt, {
              type: showChatsType?.value === 'bar' ? "shadow" : 'line',
            } as any);
            if (showChatsType?.value === 'bar') {
              // Pad y-axis so bar/total labels are not clipped at the chart edge.
              applyValueAxisBoundaryGap(opt);
            }
            opt = appendStackedTotalLabelSeries(opt, {
              enabled:
                !!showChatsType?.format
                && showChatsType?.value === 'bar',
              seriesName: '__total_label__',
              targetSeriesNames: [
                'R&D Staff Cost',
                'Outsourcing',
                'Other R&D',
                'G&A Overheads',
                'VGTR',
              ],
              labelFormatter: (value: number) => mathFormat(value),
            });
            return opt;
          }}
        ></Chart>}

      {!loading && showChatsType?.value === 'table' && <TableContent
        className="dbs-commons-table dbs-chats-table"
        key={`${showChatsType?.value}`}
        emptyMinHeight={chartHeight}
        // height={'360px'}
        // ref={tableContent}
        // searchForm={searchForm}
        datas={tableData}
        columns={columns}
        sort={[{ by: 'fill_time', order: 'desc' }]}
        resultKey="list"
        recordKey="date"
        addons={[
          // 支持表格滚动，高度超过 192 开始显示滚动条
          // scrollable({
          //   // maxHeight: '310px',
          //   // minWidth: 2000,
          // }),
          ...tableAddons,
        ]}
      ></TableContent>}
    </div>
  );
};

export default SpendTrendCard;
